<template>
  <div class="automations-manager">
    <div class="flex flex-wrap align-items-center justify-space-between gap-sm m-b-m">
      <div class="min-w-0">
        <h1 class="font-heavy m-y-none">Automations</h1>
        <p class="text-gray-500 m-t-xxs m-b-none">
          Rules that set a private wrestler property when a wrestler's roster memberships change.
        </p>
      </div>
      <div class="flex flex-wrap gap-xs">
        <button type="button" class="btn btn-default" :disabled="reevaluating || loading" @click="confirmReevaluate">
          <spinner v-if="reevaluating" :inline="true"></spinner>
          Re-evaluate wrestlers
        </button>
        <button type="button" class="btn btn-primary" :disabled="selectedIds.length === 0" @click="showDuplicate = true">
          Duplicate selected<template v-if="selectedIds.length > 0"> ({{ selectedIds.length }})</template>
        </button>
      </div>
    </div>

    <div v-if="notice" class="alert alert-dismissable m-b-m" :class="notice.type === 'success' ? 'alert-success' : 'alert-info'" role="status">
      <button type="button" class="close" aria-label="Dismiss" @click="notice = null">&times;</button>
      {{ notice.text }}
    </div>
    <div v-if="error" class="alert alert-danger m-b-m" role="alert">{{ errorMessage }}</div>

    <div v-if="loading" class="text-center p-m">
      <spinner></spinner>
    </div>
    <p v-else-if="automations.length === 0 && !error" class="text-gray-500 p-m m-b-none">
      No automations yet.
    </p>
    <template v-else>
      <div class="automations-list-header">
        <label class="automations-checkbox-label m-b-none">
          <input type="checkbox"
                 :checked="allSelected"
                 :indeterminate.prop="someSelected && !allSelected"
                 @change="toggleAll($event.target.checked)" />
          <span>{{ selectedIds.length > 0 ? `${selectedIds.length} selected` : 'Select all' }}</span>
        </label>
        <span class="hidden-xs">Status</span>
      </div>

      <!-- Grouped by the property each automation writes, so last year's and this year's sets
           (which share names after a rollover) are easy to tell apart. -->
      <section v-for="group in groups" :key="group.key" class="automations-group">
        <div class="automations-group__header">
          <label class="automations-checkbox-label m-b-none">
            <input type="checkbox"
                   :checked="groupAllSelected(group)"
                   :indeterminate.prop="groupSomeSelected(group) && !groupAllSelected(group)"
                   :aria-label="`Select all automations writing to ${group.label}`"
                   @change="toggleGroup(group, $event.target.checked)" />
            <span>Writes to <strong>{{ group.label }}</strong></span>
          </label>
          <span class="text-12 text-gray-500">{{ groupCounts(group) }}</span>
        </div>

        <div v-for="automation in group.automations"
             :key="`automation-${automation.id}`"
             class="automations-row"
             :class="{ 'is-selected': selectedIds.includes(automation.id) }">
          <input type="checkbox"
                 class="automations-row__check"
                 :value="automation.id"
                 v-model="selectedIds"
                 :aria-label="`Select ${automation.name}`" />
          <div class="flex-1 min-w-0">
            <div class="flex flex-wrap align-items-center gap-xs">
              <span class="font-semi text-gray-900">{{ automation.name }}</span>
              <span class="badge-gray">Priority {{ automation.priority }}</span>
            </div>
            <p v-if="automation.description" class="text-gray-600 m-t-xxs m-b-xs">{{ automation.description }}</p>
            <p class="automations-row__criteria m-b-xxs">
              <span class="text-gray-500">When</span> {{ criteriaSummary(automation) }}
            </p>
            <p class="m-b-xxs">
              <span class="text-gray-500">Writes</span>
              {{ actionQuestionPrompt(automation) }} &rarr; <strong>{{ actionValue(automation) }}</strong>
            </p>
            <p class="text-12 text-gray-500 m-b-none">
              {{ triggerLabel(automation.trigger_type) }}
              <template v-if="skipValues(automation).length > 0">
                &middot; Skips wrestlers already set to {{ skipValues(automation).join(', ') }}
              </template>
            </p>
            <p v-if="rowErrors[automation.id]" class="text-danger m-t-xxs m-b-none" role="alert">{{ rowErrors[automation.id] }}</p>
          </div>
          <div class="automations-row__status">
            <span :class="automation.status === 'active' ? 'badge-green' : 'badge-yellow'">
              {{ automation.status === 'active' ? 'Active' : 'Draft' }}
            </span>
            <button type="button"
                    class="btn btn-default btn-xs"
                    :disabled="!!toggling[automation.id]"
                    @click="toggleStatus(automation)">
              <spinner v-if="toggling[automation.id]" :inline="true"></spinner>
              {{ automation.status === 'active' ? 'Move to draft' : 'Activate' }}
            </button>
          </div>
        </div>
      </section>
    </template>

    <automations-duplicate-modal
      v-if="showDuplicate"
      :automations="selectedAutomations"
      @close="showDuplicate = false"
      @created="onDuplicated">
    </automations-duplicate-modal>
  </div>
</template>

<script>
import swal from 'sweetalert2';
import Spinner from './spinner.vue';
import AutomationsDuplicateModal from './automations_duplicate_modal.vue';
import { errorableMix } from './mixins/errorable_mix';

const OPERATOR_SYMBOLS = {
  greater_than_or_equal_to: '≥',
  greater_than: '>',
  less_than: '<',
  less_than_or_equal_to: '≤',
  equals: '=',
  equal_to: '=',
};

const TRIGGER_LABELS = {
  'roster_memberships.changed': 'Runs when roster memberships change',
  'private_property.changed': 'Runs when a private property changes',
};

export default {
  name: 'automations-manager',
  components: { Spinner, AutomationsDuplicateModal },
  mixins: [errorableMix],
  data() {
    return {
      loading: true,
      automations: [],
      selectedIds: [],
      // { [automationId]: true } while a status PATCH is in flight
      toggling: {},
      // { [automationId]: 'message' } for failed status changes
      rowErrors: {},
      showDuplicate: false,
      reevaluating: false,
      // { type: 'success' | 'info', text }
      notice: null,
    };
  },
  computed: {
    // Groups keyed by action question, in the order they first appear (list is priority ordered)
    groups() {
      const groups = [];
      const byKey = {};
      this.automations.forEach((automation) => {
        const question = automation.action_question;
        const key = question ? `q-${question.id}` : 'q-none';
        if (!byKey[key]) {
          byKey[key] = { key, label: question ? question.prompt : 'Unknown property', automations: [] };
          groups.push(byKey[key]);
        }
        byKey[key].automations.push(automation);
      });
      return groups;
    },
    selectedAutomations() {
      return this.automations.filter((a) => this.selectedIds.includes(a.id));
    },
    allSelected() {
      return this.automations.length > 0 && this.selectedIds.length === this.automations.length;
    },
    someSelected() {
      return this.selectedIds.length > 0;
    },
  },
  created() {
    this.loadAutomations();
  },
  methods: {
    loadAutomations() {
      const vm = this;
      vm.loading = true;
      vm.clearError();
      return vm.$apiService.loadAllPages(`${vm.$apiService.automationsUrl()}?per_page=100`, 'automations', [])
        .then((automations) => {
          vm.automations = automations;
          // Drop selections for automations that no longer exist
          const ids = vm.automations.map((a) => a.id);
          vm.selectedIds = vm.selectedIds.filter((id) => ids.includes(id));
          vm.loading = false;
        })
        .catch(() => {
          vm.loading = false;
          vm.showError('Unable to load automations. Please refresh the page.');
        });
    },
    toggleAll(checked) {
      this.selectedIds = checked ? this.automations.map((a) => a.id) : [];
    },
    groupAllSelected(group) {
      return group.automations.every((a) => this.selectedIds.includes(a.id));
    },
    groupSomeSelected(group) {
      return group.automations.some((a) => this.selectedIds.includes(a.id));
    },
    toggleGroup(group, checked) {
      const groupIds = group.automations.map((a) => a.id);
      const others = this.selectedIds.filter((id) => !groupIds.includes(id));
      this.selectedIds = checked ? others.concat(groupIds) : others;
    },
    groupCounts(group) {
      const active = group.automations.filter((a) => a.status === 'active').length;
      const draft = group.automations.length - active;
      return `${active} active · ${draft} draft`;
    },
    // Condition sets are ANDed internally and joined by the logical operator, e.g.
    // "(S2526 ≥ 2 AND EN2526 ≥ 1) OR S2526 ≥ 3"
    criteriaSummary(automation) {
      const criteria = (automation.trigger_data && automation.trigger_data.criteria) || {};
      const sets = (criteria.conditions || []).filter((set) => set && set.length > 0);
      if (sets.length === 0) return 'No conditions';

      const joiner = ` ${(criteria.logical_condition_operator || 'or').toUpperCase()} `;
      return sets.map((set) => {
        const text = set.map((condition) => this.conditionSummary(automation, condition)).join(' AND ');
        return sets.length > 1 && set.length > 1 ? `(${text})` : text;
      }).join(joiner);
    },
    conditionSummary(automation, condition) {
      const symbol = OPERATOR_SYMBOLS[condition.operator] || condition.operator;
      if (condition.type === 'target_registration_answer_value') {
        // Checks the wrestler's current answer on the trigger property
        const triggerId = automation.trigger_data && automation.trigger_data.trigger_registration_question_id;
        const question = automation.action_question;
        const label = question && String(question.id) === String(triggerId) ? question.prompt : 'Current value';
        return `${label} ${symbol} ${condition.value}`;
      }
      return `${condition.tag_abbreviation} ${symbol} ${condition.value}`;
    },
    actionQuestionPrompt(automation) {
      return automation.action_question ? automation.action_question.prompt : 'Unknown property';
    },
    actionValue(automation) {
      return (automation.action_data && automation.action_data.action_registration_answer_value) || '(blank)';
    },
    skipValues(automation) {
      const criteria = (automation.trigger_data && automation.trigger_data.criteria) || {};
      return criteria.early_return_if_target_value_already_equals || [];
    },
    triggerLabel(triggerType) {
      return TRIGGER_LABELS[triggerType] || triggerType;
    },
    // parseErrorResponse assumes a server response; network failures get the generic message
    errorText(error) {
      return error && error.response ? this.parseErrorResponse(error) : 'Something went wrong. Please try again.';
    },
    toggleStatus(automation) {
      const vm = this;
      const status = automation.status === 'active' ? 'draft' : 'active';
      vm.$set(vm.toggling, automation.id, true);
      vm.$delete(vm.rowErrors, automation.id);

      axios.patch(vm.$apiService.automationUrl(automation.id), { automation: { status } })
        .then(() => {
          automation.status = status;
          vm.$delete(vm.toggling, automation.id);
        })
        .catch((error) => {
          vm.$delete(vm.toggling, automation.id);
          vm.$set(vm.rowErrors, automation.id, vm.errorText(error));
        });
    },
    onDuplicated(copies) {
      const vm = this;
      vm.showDuplicate = false;
      vm.selectedIds = [];
      const count = copies.length;
      vm.loadAutomations().then(() => {
        vm.notice = {
          type: 'success',
          text: `Created ${count} draft ${count === 1 ? 'automation' : 'automations'}. Review them, then activate the new ones and move last year's to draft.`,
        };
      });
    },
    confirmReevaluate() {
      const vm = this;
      swal.fire({
        icon: 'question',
        title: 'Re-evaluate wrestlers?',
        text: 'This re-runs every active automation for all wrestlers already on rosters those automations count. Use it after activating a new season\'s automations and moving last year\'s to draft, so current wrestlers get updated.',
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: 'Re-evaluate wrestlers',
        cancelButtonText: 'Cancel',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-primary m-l-md',
          cancelButton: 'btn btn-default',
        },
      }).then((result) => {
        if (result.value) vm.reevaluate();
      });
    },
    reevaluate() {
      const vm = this;
      vm.reevaluating = true;
      vm.clearError();
      axios.post(`${vm.$apiService.automationsUrl()}/reevaluate`)
        .then((response) => {
          vm.reevaluating = false;
          const count = response.data.profile_count || 0;
          vm.notice = count > 0
            ? { type: 'success', text: `Re-evaluating ${count} ${count === 1 ? 'wrestler' : 'wrestlers'}. Results appear in the job log below (refresh to see progress).` }
            : { type: 'info', text: 'No wrestlers to re-evaluate. No active automation counts a tag that is on a roster.' };
        })
        .catch((error) => {
          vm.reevaluating = false;
          vm.showError(vm.errorText(error));
        });
    },
  },
};
</script>
